//* Libraries imports
import { useQuery } from "@tanstack/react-query";

//* Schemas Import
import { paginatedEssay } from "../../../schemas/common/essay/essay";

//* Pagination schemas
import { laravelPaginationMetaSchema } from "../../../schemas/common/paginated-data";

//* Axios Import
import { privateApi } from "../../../services/apis/configs/apiClient";

//* Zod Import
import { z } from "zod";

//* Interface for pagination parameters
type GetAllParams = {
  page?: number;
  perPage?: number;
  search?: string;
}

const processExamResponseSchema = z.object({
  result: z.boolean(),
  data: z.object({
    essays: z.array(paginatedEssay),
    pagination: laravelPaginationMetaSchema,
  }),
});

/** 
 * API for process essays
 */
const examAPI = {
  /**
   * Gets all process essays with pagination support
   * @param processoId Process ID
   * @param params Pagination parameters (page, items per page, search term)
   * @returns Paginated list of process essays
   */
  getAll: async function (processoId: number, params?: GetAllParams) {
    const response = await privateApi.get(
      `/student_portal/private/processos/${processoId}/redacoes`,
      {
        params: {
          page: params?.page || 1,
          limit: params?.perPage || 10,
          search: params?.search || "",
        },
      }
    );

    const validatedResponse = processExamResponseSchema.parse({
      result: true,
      data: {
        essays: response.data.essays,
        pagination: response.data.pagination,
      },
    });

    console.log("Response from API:", validatedResponse);
    
    return {
      data: validatedResponse.data.essays,
      meta: validatedResponse.data.pagination,
    };
  }
}

/**
 * Hook to get paginated process essays
 * @param processoId Process ID
 * @param page Current page number
 * @param perPage Items per page
 * @param search Search term (optional) - minimum 3 characters to trigger search
 * @returns Query for paginated process essays
 */
export function useGetPaginatedProcessExams(
  processoId: number,
  page: number = 1,
  perPage: number = 10,
  search: string = ""
) {
  const shouldSearch = search.length === 0 || search.length >= 3;
  
  return useQuery({
    queryKey: ["paginated-process-essays", processoId, page, perPage, search],
    queryFn: () =>
      examAPI.getAll(processoId, {
        page,
        perPage,
        search: shouldSearch ? search : "",
      }),
    enabled: shouldSearch,
    retry: 3,
    staleTime: 1000 * 60 * 1,
    gcTime: 1000 * 60 * 10,
  });
}
